import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Tag, ChevronRight, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = '/api';

const CATEGORIES = [
  { value: '', label: 'All Posts' },
  { value: 'market-trends', label: 'Market Trends' },
  { value: 'tenant-tips', label: 'Tenant Tips' },
  { value: 'owner-guide', label: 'Owner Guide' },
  { value: 'legal', label: 'Legal & Finance' },
  { value: 'general', label: 'General' },
];

function BlogCard({ blog, featured }) {
  const date = new Date(blog.published_at || blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (featured) {
    return (
      <Link to={`/blog/${blog.slug}`} className="group block" data-testid={`featured-blog-${blog.blog_id}`}>
        <div className="relative rounded-3xl overflow-hidden h-80 bg-slate-100">
          {blog.cover_image ? (
            <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
              <BookOpen className="w-20 h-20 text-white/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            {blog.category && (
              <span className="text-xs font-bold uppercase tracking-widest text-blue-300 mb-2 block">{blog.category.replace('-', ' ')}</span>
            )}
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-blue-200 transition-colors line-clamp-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              {blog.title}
            </h2>
            <p className="text-slate-300 text-sm line-clamp-2 mb-3">{blog.excerpt}</p>
            <div className="flex items-center gap-3 text-slate-400 text-xs">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{blog.read_time} min read</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${blog.slug}`} className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col" data-testid={`blog-card-${blog.blog_id}`}>
      <div className="relative h-48 bg-slate-100 overflow-hidden">
        {blog.cover_image ? (
          <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-blue-300" />
          </div>
        )}
        {blog.category && (
          <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-blue-700 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
            {blog.category.replace('-', ' ')}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-bold text-slate-900 text-base line-clamp-2 mb-2 group-hover:text-blue-700 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {blog.title}
        </h3>
        <p className="text-slate-500 text-sm line-clamp-3 flex-1 mb-4">{blog.excerpt}</p>
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex items-center gap-3 text-slate-400 text-xs">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{blog.read_time || 1} min</span>
          </div>
          <span className="text-blue-700 text-xs font-semibold flex items-center gap-0.5">
            Read <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const category = searchParams.get('category') || '';

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 9 });
    if (category) params.set('category', category);
    fetch(`${API}/blogs?${params}`)
      .then(r => r.json())
      .then(d => { setBlogs(d.blogs || []); setTotal(d.total || 0); setPages(d.pages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, category]);

  const setPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p);
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const setCategory = (c) => {
    const params = new URLSearchParams();
    if (c) params.set('category', c);
    setSearchParams(params);
  };

  const [featured, ...rest] = blogs;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Real Estate Blog | Rent Mitra</title>
        <meta name="description" content="Expert insights, tips, and guides for tenants and property owners in India. Stay informed about rental trends, legal tips, and market updates." />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
        <meta property="og:title" content="Real Estate Blog — Rent Mitra" />
        <meta property="og:description" content="Expert tips, tenant guides, and rental market insights for India." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/blog`} />
        <meta property="og:site_name" content="Rent Mitra" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Real Estate Blog — Rent Mitra" />
        <meta name="twitter:description" content="Expert tips, tenant guides, and rental market insights for India." />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <BookOpen className="w-4 h-4" /> Real Estate Insights
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Rent Mitra Blog
            </h1>
            <p className="text-slate-500 text-lg">Tips, guides, and market insights for smarter renting in India.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 scrollbar-thin scrollbar-thumb-slate-200">
          {CATEGORIES.map(c => (
            <button
              key={c.value}
              onClick={() => setCategory(c.value)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                category === c.value
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700'
              }`}
              data-testid={`blog-cat-${c.value || 'all'}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
                <div className="h-48 bg-slate-100" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">No posts yet</h3>
            <p className="text-slate-400">Check back soon for expert real estate insights!</p>
          </div>
        ) : (
          <>
            {/* Featured post */}
            {featured && page === 1 && !category && (
              <div className="mb-8">
                <BlogCard blog={featured} featured />
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(page === 1 && !category ? rest : blogs).map(blog => (
                <BlogCard key={blog.blog_id} blog={blog} />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button onClick={() => setPage(page - 1)} disabled={page === 1}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  Prev
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${p === page ? 'bg-blue-700 text-white' : 'border border-slate-200 text-slate-600 hover:border-blue-300'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(page + 1)} disabled={page === pages}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
